import { categories as localCategories } from './listingCategories';

/**
 * P7 cutover — runtime category/listing-field configuration.
 *
 * TradTree's output is baked into this repo as two data files
 * (listingCategories.js and configListing.js listingFields). With
 * CATEGORIES_SOURCE=admin the app instead fetches the admin panel's config at
 * boot (SSR: server/dataLoader.js hydrates this module directly; CSR:
 * src/index.js fetches GET /api/listing-config before first render) and every
 * consumer — mergeConfig, the Topbar menu, category landing URLs, the
 * hosted-assets selector — reads through the getters below. When nothing has
 * been hydrated (flag off, panel unreachable with no cache, malformed
 * payload), the getters return the baked-in data unchanged: TradTree remains
 * the fallback at every step.
 *
 * The panel emits the exact baked-in shapes (nested categories with
 * {name, id, url_slug, level, pageConfig?, subcategories}; fields in the
 * configListing object shape), so no transformation happens here — only
 * substitution and one merge rule for fields (see applyRuntimeListingFields).
 */

let runtime = null; // { categories, listingFields } | null

// listingFields must be non-empty too: applyRuntimeListingFields drops every
// baked-in category-scoped field when hydrated, so an empty panel array would
// wipe all search filters / edit-listing detail fields.
const isValidPayload = payload =>
  payload &&
  typeof payload === 'object' &&
  Array.isArray(payload.categories) &&
  payload.categories.length > 0 &&
  Array.isArray(payload.listingFields) &&
  payload.listingFields.length > 0;

/**
 * Install (or clear, when given null/invalid data) the runtime config.
 * Called by server/dataLoader.js (SSR) and src/index.js (CSR) before render.
 */
export const hydrateListingConfig = payload => {
  runtime = isValidPayload(payload)
    ? { categories: payload.categories, listingFields: payload.listingFields }
    : null;
};

/** The category tree — panel data when hydrated, baked-in file otherwise. */
export const getCategories = () => runtime?.categories || localCategories;

/**
 * Merge rule for listing fields: the panel owns the CATEGORY-SCOPED fields
 * (the ones carrying a categoryConfig); built-in/computed local fields
 * without a categoryConfig stay from the baked-in list. On a key collision
 * the panel wins. Not hydrated → baked-in list returned untouched.
 *
 * @param {Array} localFields - defaultConfigs.listing.listingFields
 */
export const applyRuntimeListingFields = localFields => {
  const local = Array.isArray(localFields) ? localFields : [];
  if (!runtime) return local;
  const panelKeys = new Set(runtime.listingFields.map(f => f.key));
  const localNonCategory = local.filter(f => !f.categoryConfig && !panelKeys.has(f.key));
  return [...runtime.listingFields, ...localNonCategory];
};

/** True when the panel's config is live (used only for logging/diagnostics). */
export const isRuntimeListingConfigActive = () => runtime !== null;
